import { useCallback, useMemo } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { Box, Skeleton, useTheme } from '@/ui/primitives';
import { s } from '../../../theme/tokens';
import { DataGrid } from '../../primitives/DataGrid';
import { resolveGridColumnAlignment, wrapDataGridColumns } from './wrapDataGridColumns';
import type { SpacingToken } from '@/theme/tokens';
import type {
  DataGridProps,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridValidRowModel,
  SxProps,
  Theme,
} from '@/ui/primitives';
import type { ReactElement } from 'react';

type LoadingRow = { id: string; __loading: true };

const skeletonWidthSteps = [38, 44, 51, 58, 65, 72, 79] as const;
const CLONE_TABLE_SKELETON_HEIGHT = 12;
const CLONE_TABLE_BOTTOM_PADDING = '10px';
const CLONE_TABLE_SKELETON_TEXT_PRIMARY_WIDTH = '88%';
const CLONE_TABLE_SKELETON_TEXT_SECONDARY_WIDTH = '72%';
const CLONE_TABLE_SKELETON_STATUS_PILL_WIDTH = '72px';
const CLONE_TABLE_SKELETON_STATUS_META_WIDTH = '44px';
const CLONE_TABLE_SKELETON_STATUS_TIMESTAMP_WIDTH = '56px';
const CLONE_TABLE_SKELETON_SPARKLINE_LABEL_WIDTH = '92px';
const CLONE_TABLE_SKELETON_SPARKLINE_LINE_WIDTH = '84%';
const CLONE_TABLE_SKELETON_TAG_WIDTH = '54px';
const CLONE_TABLE_SKELETON_ACTION_SIZE = '18px';
const CLONE_TABLE_SKELETON_RADIUS = '999px';
const CLONE_TABLE_SKELETON_STATUS_INDENT = '2px';
const CLONE_TABLE_SKELETON_PRIMARY_HEADER_WIDTH = '112px';
const CLONE_TABLE_PAGINATION_SKELETON_WIDTH = '212px';

const cloneSkeletonColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  boxSizing: 'border-box',
  paddingBottom: CLONE_TABLE_BOTTOM_PADDING,
} as const;

const hashSkeletonKey = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const resolveCellSkeletonWidth = (params: { id: GridRowId; field: string }): string => {
  const key = `${String(params.id)}:${params.field}`;
  const width = skeletonWidthSteps[hashSkeletonKey(key) % skeletonWidthSteps.length];
  return `${width}%`;
};

const isCloneSparklineCell = (cellClassName: string | undefined): boolean =>
  typeof cellClassName === 'string' &&
  [
    'tests-table-clone-passrate-cell',
    'tests-table-clone-frequency-cell',
    'tests-table-clone-avgduration-cell',
  ].includes(cellClassName);

const isCloneStructuredCell = (field: string, cellClassName: string | undefined): boolean =>
  cellClassName === 'tests-table-clone-test-cell' ||
  cellClassName === 'clone-execution-status-cell' ||
  field === 'latestExecutionStatus' ||
  field === 'latestBuildStatus' ||
  isCloneSparklineCell(cellClassName) ||
  field === 'actions';

const renderCloneCellSkeleton = (
  params: { id: GridRowId; field: string },
  options: {
    field: string;
    cellClassName: string | undefined;
    cellJustifyContent: 'flex-start' | 'center' | 'flex-end';
  },
): JSX.Element => {
  if (options.cellClassName === 'tests-table-clone-test-cell') {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: '8px',
            gap: '6px',
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_TEXT_PRIMARY_WIDTH}
          sx={{
            height: CLONE_TABLE_SKELETON_HEIGHT,
            transform: 'none',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
          }}
        />
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_TEXT_SECONDARY_WIDTH}
          sx={{
            height: CLONE_TABLE_SKELETON_HEIGHT,
            transform: 'none',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
          }}
        />
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_TAG_WIDTH}
          height={CLONE_TABLE_SKELETON_HEIGHT}
          sx={{ transform: 'none', borderRadius: CLONE_TABLE_SKELETON_RADIUS }}
        />
      </Box>
    );
  }

  if (options.field === 'latestExecutionStatus' || options.field === 'latestBuildStatus') {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: '6px',
            pl: CLONE_TABLE_SKELETON_STATUS_INDENT,
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_STATUS_PILL_WIDTH}
          height={CLONE_TABLE_SKELETON_HEIGHT}
          sx={{
            transform: 'none',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
            mt: '4px',
            mb: '6px',
            maxWidth: 'calc(100% - 12px)',
          }}
        />
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_STATUS_META_WIDTH}
          sx={{
            height: CLONE_TABLE_SKELETON_HEIGHT,
            transform: 'none',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
          }}
        />
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_STATUS_TIMESTAMP_WIDTH}
          sx={{
            height: CLONE_TABLE_SKELETON_HEIGHT,
            transform: 'none',
            mt: '6px',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
          }}
        />
      </Box>
    );
  }

  if (options.cellClassName === 'clone-execution-status-cell') {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: '6px',
            pl: CLONE_TABLE_SKELETON_STATUS_INDENT,
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_STATUS_PILL_WIDTH}
          height={CLONE_TABLE_SKELETON_HEIGHT}
          sx={{
            transform: 'none',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
            mt: '4px',
            mb: '6px',
            maxWidth: 'calc(100% - 12px)',
          }}
        />
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_STATUS_META_WIDTH}
          sx={{
            height: CLONE_TABLE_SKELETON_HEIGHT,
            transform: 'none',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
          }}
        />
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_STATUS_TIMESTAMP_WIDTH}
          sx={{
            height: CLONE_TABLE_SKELETON_HEIGHT,
            transform: 'none',
            mt: '6px',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
          }}
        />
      </Box>
    );
  }

  if (isCloneSparklineCell(options.cellClassName)) {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: '8px',
            justifyContent: 'space-between',
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_SPARKLINE_LABEL_WIDTH}
          sx={{
            height: CLONE_TABLE_SKELETON_HEIGHT,
            transform: 'none',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
          }}
        />
        <Box
          sx={{
            height: '34px',
            width: '100%',
            display: 'flex',
            alignItems: 'flex-end',
            pb: '0px',
          }}
        >
          <Skeleton
            variant="rounded"
            width={CLONE_TABLE_SKELETON_SPARKLINE_LINE_WIDTH}
            height={CLONE_TABLE_SKELETON_HEIGHT}
            sx={{
              transform: 'none',
              borderRadius: CLONE_TABLE_SKELETON_RADIUS,
              alignSelf: options.cellJustifyContent,
              mb: '1px',
            }}
          />
        </Box>
      </Box>
    );
  }

  if (options.field === 'actions') {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: '12px',
            alignItems: 'center',
            justifyContent: 'flex-start',
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={CLONE_TABLE_SKELETON_ACTION_SIZE}
          height={CLONE_TABLE_SKELETON_ACTION_SIZE}
          sx={{
            transform: 'none',
            borderRadius: CLONE_TABLE_SKELETON_RADIUS,
          }}
        />
      </Box>
    );
  }

  return (
    <Skeleton
      variant="rounded"
      width={resolveCellSkeletonWidth(params)}
      sx={{
        height: componentSizes.dataGrid.skeletonHeight,
        transform: 'none',
        maxWidth: '100%',
        borderRadius: CLONE_TABLE_SKELETON_RADIUS,
      }}
    />
  );
};

type SkeletonDataGridProps<T extends GridValidRowModel> = {
  columns: Array<GridColDef<T>>;
  loadingRowCount?: number;
  totalRowCount?: number;
  pageSizeOptions?: number[];
  pageSize?: number;
  paginationModel?: GridPaginationModel;
  autoHeight?: boolean;
  showToolbar?: boolean;
  getRowId: (row: T) => GridRowId;
  getRowHeight?: DataGridProps['getRowHeight'];
  cellPaddingY?: SpacingToken;
  columnVisibilityModel?: DataGridProps['columnVisibilityModel'];
  slotProps?: DataGridProps['slotProps'];
  localeText?: DataGridProps['localeText'];
  sx?: DataGridProps['sx'];
  hideFooter?: boolean;
};

const resolvePadding = (token: SpacingToken): string | number => {
  switch (token) {
    case 'none':
      return 0;
    case 'xxxs':
      return s('xxs');
    case 'xxs':
    case 'xs':
      return s('xs');
    case 'sm':
      return s('sm');
    case 'md':
      return s('md');
    case 'lg':
      return s('lg');
    case 'xl':
      return s('xl');
    case '2xl':
      return s('2xl');
    case '3xl':
      return s('3xl');
    case '4xl':
      return s('4xl');
    case '5xl':
      return s('5xl');
    case 'gutter':
      return s('gutter');
    case 'section':
      return s('section');
    default:
      return s('3xl');
  }
};

export const SkeletonDataGrid = <T extends GridValidRowModel>({
  columns,
  loadingRowCount = 10,
  totalRowCount,
  pageSizeOptions = [5, 10, 25, 50],
  pageSize,
  paginationModel,
  autoHeight = false,
  showToolbar = false,
  getRowId,
  getRowHeight,
  cellPaddingY,
  columnVisibilityModel,
  slotProps,
  localeText,
  sx,
  hideFooter = false,
}: SkeletonDataGridProps<T>): ReactElement => {
  const theme = useTheme();
  const resolvedPageSize =
    pageSize && pageSizeOptions.includes(pageSize) ? pageSize : (pageSizeOptions[0] ?? 5);

  const loadingRows = useMemo<LoadingRow[]>(
    () =>
      Array.from({ length: loadingRowCount }, (_value, index) => ({
        id: `loading-${index}`,
        __loading: true,
      })),
    [loadingRowCount],
  );

  const columnsWithLoading = useMemo(() => {
    const skeletonColumns = columns.map((column) => {
      const { align, headerAlign } = resolveGridColumnAlignment(column);
      const resolvedCellClassName =
        typeof column.cellClassName === 'string' ? column.cellClassName : undefined;
      const isStructuredCloneCell = isCloneStructuredCell(column.field, resolvedCellClassName);
      const cellJustifyContent =
        align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
      const headerJustifyContent =
        headerAlign === 'center' ? 'center' : headerAlign === 'right' ? 'flex-end' : 'flex-start';
      return {
        ...column,
        renderHeader: (params) => {
          if (column.field === 'actions') {
            return <Box sx={{ width: '100%', height: '100%' }} />;
          }
          const computedWidth = params.colDef.computedWidth;
          const widthSource =
            typeof computedWidth === 'number'
              ? computedWidth
              : typeof column.width === 'number'
                ? column.width
                : typeof column.minWidth === 'number'
                  ? column.minWidth
                  : null;
          const headerSkeletonWidth =
            resolvedCellClassName === 'tests-table-clone-test-cell'
              ? CLONE_TABLE_SKELETON_PRIMARY_HEADER_WIDTH
              : typeof widthSource === 'number'
                ? Math.max(
                    componentSizes.dataGrid.columns.xs,
                    Math.min(widthSource * 0.6, componentSizes.dataGrid.columns.max),
                  )
                : '60%';
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: headerJustifyContent,
                height: '100%',
                width: '100%',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Skeleton
                variant="rounded"
                width={headerSkeletonWidth}
                sx={{
                  height: componentSizes.dataGrid.skeletonHeight,
                  transform: 'none',
                  maxWidth: '100%',
                  borderRadius: CLONE_TABLE_SKELETON_RADIUS,
                }}
              />
            </Box>
          );
        },
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: isStructuredCloneCell ? 'flex-start' : 'center',
              justifyContent:
                column.field === 'actions'
                  ? 'center'
                  : isStructuredCloneCell
                    ? 'flex-start'
                    : cellJustifyContent,
              height: '100%',
              width: '100%',
              flex: 1,
              minWidth: 0,
              overflow: isStructuredCloneCell ? 'visible' : 'hidden',
            }}
          >
            {renderCloneCellSkeleton(params, {
              field: column.field,
              cellClassName: resolvedCellClassName,
              cellJustifyContent,
            })}
          </Box>
        ),
      };
    }) as Array<GridColDef<T>>;
    return wrapDataGridColumns(skeletonColumns, {
      getBaseCellSx: () => (cellPaddingY ? { py: resolvePadding(cellPaddingY) } : undefined),
    });
  }, [cellPaddingY, columns]);

  const resolveRowId = useCallback(
    (row: T) => {
      if ('__loading' in row) {
        return (row as unknown as LoadingRow).id;
      }
      return getRowId(row);
    },
    [getRowId],
  );

  const baseSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    '& .MuiDataGrid-footerContainer': {
      borderTop: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
    },
  };
  const resolvedSx: SxProps<Theme> = [
    baseSx,
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as SxProps<Theme>;
  const resolvedPaginationModel = paginationModel ?? { page: 0, pageSize: resolvedPageSize };
  const resolvedRowCount = typeof totalRowCount === 'number' ? totalRowCount : loadingRows.length;
  const showFooterSkeleton = !hideFooter && typeof totalRowCount !== 'number';
  const defaultSlotProps: NonNullable<DataGridProps['slotProps']> | null =
    typeof totalRowCount === 'number'
      ? null
      : {
          pagination: {
            labelDisplayedRows: () => '',
          },
        };
  const resolvedSlotProps: DataGridProps['slotProps'] = defaultSlotProps
    ? slotProps
      ? {
          ...defaultSlotProps,
          ...slotProps,
          pagination: {
            ...defaultSlotProps.pagination,
            ...(slotProps.pagination ?? {}),
          },
        }
      : defaultSlotProps
    : slotProps;

  return (
    <>
      <DataGrid
        rows={loadingRows as unknown as T[]}
        columns={columnsWithLoading}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooterSelectedRowCount
        hideFooter={hideFooter || showFooterSkeleton}
        pageSizeOptions={pageSizeOptions}
        paginationMode="server"
        paginationModel={resolvedPaginationModel}
        rowCount={resolvedRowCount}
        getRowId={resolveRowId}
        initialState={{ pagination: { paginationModel: resolvedPaginationModel } }}
        autoHeight={autoHeight}
        getRowHeight={getRowHeight}
        showToolbar={showToolbar}
        columnVisibilityModel={columnVisibilityModel}
        slotProps={resolvedSlotProps}
        localeText={localeText}
        sx={resolvedSx}
      />
      {showFooterSkeleton ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            minHeight: `${componentSizes.dataGrid.footerHeight}px`,
            height: `${componentSizes.dataGrid.footerHeight}px`,
            maxHeight: `${componentSizes.dataGrid.footerHeight}px`,
            paddingLeft: s('xxs'),
            paddingRight: s('none'),
            paddingTop: s('none'),
            paddingBottom: s('none'),
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <Skeleton
            variant="rounded"
            width={CLONE_TABLE_PAGINATION_SKELETON_WIDTH}
            height={CLONE_TABLE_SKELETON_HEIGHT}
            sx={{ transform: 'none', borderRadius: CLONE_TABLE_SKELETON_RADIUS }}
          />
        </Box>
      ) : null}
    </>
  );
};
